/**
 * Health Page — the entire user-visible surface of Milestone 1.
 *
 * Fetches ``/api/v1/health`` and ``/api/v1/version`` and renders:
 *   - the backend version + milestone + environment
 *   - a "live" pulse when the DB responds and the health endpoint is reachable
 *   - the adapter status matrix (AI / storage / email / social / auth)
 *   - the prompt-registry count
 *   - a chrome strip showing the correlation ID for the last request
 *
 * The page is a *composition* — data comes from ``@/shared/api``, and every
 * visual atom is a shadcn primitive. No business logic lives here.
 */
import { useQuery } from "@tanstack/react-query";
import { Activity, RefreshCw } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";
import type {
  AdapterMatrix,
  AdapterStatus,
  HealthResponse,
  VersionResponse,
} from "@/shared/api";
import { ApiError, fetchHealth, fetchVersion } from "@/shared/api";
import { formatUptime } from "@/shared/lib/utils";

/** Map an :type:`AdapterStatus` to its palette + label. Pure lookup. */
const STATUS_META: Record<
  AdapterStatus,
  { label: string; ring: string; dot: string; tone: string }
> = {
  ok: { label: "Live", ring: "ring-emerald-500/40", dot: "bg-emerald-500", tone: "text-emerald-700 dark:text-emerald-400" },
  mock: { label: "Mock", ring: "ring-indigo-500/40", dot: "bg-indigo-500", tone: "text-indigo-700 dark:text-indigo-400" },
  noop: { label: "No-op", ring: "ring-slate-400/40", dot: "bg-slate-400", tone: "text-slate-600 dark:text-slate-400" },
  local: { label: "Local", ring: "ring-amber-500/40", dot: "bg-amber-500", tone: "text-amber-700 dark:text-amber-400" },
  stub: { label: "Stub", ring: "ring-violet-500/40", dot: "bg-violet-500", tone: "text-violet-700 dark:text-violet-400" },
  down: { label: "Down", ring: "ring-red-500/50", dot: "bg-red-500", tone: "text-red-700 dark:text-red-400" },
};

const ADAPTER_ORDER: Array<{ key: keyof AdapterMatrix; label: string; description: string }> = [
  { key: "ai", label: "AI Providers", description: "Text / image / video generation, embeddings, vision." },
  { key: "storage", label: "Object Storage", description: "Media, exports, uploads. Cloudflare R2 in production." },
  { key: "email", label: "Email", description: "Transactional email — invites, password resets, digests." },
  { key: "social", label: "Social Publishing", description: "LinkedIn, X, Instagram, TikTok — behind one interface." },
  { key: "auth", label: "Auth Provider", description: "JWT + OAuth. Wired in Milestone 3." },
];

export function HealthPage(): JSX.Element {
  const health = useQuery<HealthResponse, ApiError>({
    queryKey: ["health"],
    queryFn: fetchHealth,
    refetchInterval: 15_000,
  });
  const version = useQuery<VersionResponse, ApiError>({
    queryKey: ["version"],
    queryFn: fetchVersion,
  });

  const isLoading = health.isLoading || version.isLoading;
  const errored = health.isError || version.isError;

  return (
    <div className="relative min-h-[calc(100vh-8.5rem)] overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-10 bg-grid opacity-40" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-10 h-64 bg-gradient-to-b from-primary/[0.06] to-transparent" />

      <div className="mx-auto max-w-6xl px-6 py-14 md:py-20">
        <SectionHeader
          isLive={health.data?.status === "ok"}
          isFetching={health.isFetching}
          onRefresh={() => {
            void health.refetch();
            void version.refetch();
          }}
        />

        {errored ? <ErrorPanel error={health.error ?? version.error!} /> : null}

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <SummaryTile
            testId="tile-backend"
            label="Backend"
            value={version.data?.version ?? health.data?.version ?? "—"}
            hint={
              version.data
                ? `${version.data.environment} · ${version.data.milestone}`
                : "resolving…"
            }
            isLoading={isLoading}
          />
          <SummaryTile
            testId="tile-uptime"
            label="Uptime"
            value={health.data ? formatUptime(health.data.uptime_seconds) : "—"}
            hint={health.data ? "Since last cold start" : "resolving…"}
            isLoading={isLoading}
          />
          <SummaryTile
            testId="tile-db"
            label="MongoDB"
            value={health.data?.db === "ok" ? "Connected" : health.data?.db === "down" ? "Down" : "—"}
            hint="Motor async driver · indexed"
            tone={health.data?.db === "ok" ? "positive" : health.data?.db === "down" ? "critical" : "neutral"}
            isLoading={isLoading}
          />
        </div>

        <Card
          data-testid="adapter-matrix"
          className="mt-10 border-border/70 bg-card/70 shadow-sm backdrop-blur"
        >
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div className="space-y-1.5">
              <CardTitle className="font-display text-2xl font-semibold tracking-tight">
                Adapter Matrix
              </CardTitle>
              <CardDescription className="max-w-lg text-sm text-muted-foreground">
                Every vendor sits behind an interface. Milestone 1 ships mock,
                no-op, and local adapters — live providers arrive in later
                milestones without touching feature code.
              </CardDescription>
            </div>
            <Badge
              variant="outline"
              className="hidden font-mono text-[10px] uppercase tracking-widest md:inline-flex"
            >
              docs/adr/0006
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              {ADAPTER_ORDER.map(({ key, label, description }) => {
                const status = health.data?.adapters[key];
                return (
                  <AdapterRow
                    key={key}
                    testId={`adapter-${key}`}
                    label={label}
                    description={description}
                    status={status}
                    isLoading={isLoading}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <PromptRegistryPanel count={health.data?.prompts_loaded} isLoading={isLoading} />
          <CorrelationPanel correlationId={health.data?.correlation_id ?? null} isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
}

// ─── section fragments ──────────────────────────────────────────────

function SectionHeader({
  isLive,
  isFetching,
  onRefresh,
}: {
  isLive: boolean;
  isFetching: boolean;
  onRefresh: () => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div className="max-w-2xl space-y-4">
        <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/60 px-3 py-1 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
          <span
            data-testid="live-indicator"
            className={`h-1.5 w-1.5 rounded-full ${isLive ? "bg-accent" : "bg-muted-foreground/60"} ${isLive ? "animate-pulse" : ""}`}
          />
          {isLive ? "System live" : "Awaiting backend"}
        </div>
        <h1 className="font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
          Foundation is <span className="text-primary">wired</span>.
        </h1>
        <p className="max-w-xl text-base text-muted-foreground md:text-lg">
          Milestone 1 encodes the blueprint in code, tests, and CI. This page is
          the only user-visible surface — proof that the frontend, backend, and
          Mongo talk to each other through the correct abstractions.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Button
          data-testid="refresh-health"
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isFetching}
          className="gap-2"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>
    </div>
  );
}

function SummaryTile({
  testId,
  label,
  value,
  hint,
  tone,
  isLoading,
}: {
  testId: string;
  label: string;
  value: string;
  hint: string;
  tone?: "positive" | "critical" | "neutral";
  isLoading: boolean;
}): JSX.Element {
  const toneClass =
    tone === "positive"
      ? "text-emerald-700 dark:text-emerald-400"
      : tone === "critical"
        ? "text-red-700 dark:text-red-400"
        : "text-foreground";
  return (
    <Card data-testid={testId} className="border-border/70 bg-card/70 backdrop-blur">
      <CardContent className="p-6">
        <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
          {label}
        </p>
        {isLoading ? (
          <Skeleton className="mt-3 h-8 w-24" />
        ) : (
          <p className={`mt-2 font-display text-3xl font-semibold ${toneClass}`}>{value}</p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

function AdapterRow({
  testId,
  label,
  description,
  status,
  isLoading,
}: {
  testId: string;
  label: string;
  description: string;
  status: AdapterStatus | undefined;
  isLoading: boolean;
}): JSX.Element {
  const meta = status ? STATUS_META[status] : null;
  return (
    <div
      data-testid={testId}
      className={`group flex items-start justify-between gap-4 rounded-lg border border-border/60 bg-background/40 p-4 ring-1 ring-transparent transition ${meta?.ring ?? ""}`}
    >
      <div className="flex-1 space-y-1">
        <p className="font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-col items-end gap-2">
        {isLoading || !meta ? (
          <Skeleton className="h-5 w-14" />
        ) : (
          <span
            data-testid={`${testId}-status`}
            className={`inline-flex items-center gap-2 rounded-full border border-border/70 bg-background px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest ${meta.tone}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
        )}
      </div>
    </div>
  );
}

function PromptRegistryPanel({
  count,
  isLoading,
}: {
  count: number | undefined;
  isLoading: boolean;
}): JSX.Element {
  return (
    <Card data-testid="prompts-panel" className="border-border/70 bg-card/70 backdrop-blur">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display text-xl font-semibold">
          <Activity className="h-4 w-4 text-primary" /> Prompt Registry
        </CardTitle>
        <CardDescription className="text-sm">
          Prompts live as versioned YAML — never inlined in feature code.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <p className="font-display text-4xl font-semibold">
            {count ?? 0}{" "}
            <span className="text-sm font-mono font-medium uppercase tracking-widest text-muted-foreground">
              templates loaded
            </span>
          </p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Capabilities covered: text, image, video generation.
        </p>
      </CardContent>
    </Card>
  );
}

function CorrelationPanel({
  correlationId,
  isLoading,
}: {
  correlationId: string | null;
  isLoading: boolean;
}): JSX.Element {
  return (
    <Card data-testid="correlation-panel" className="border-border/70 bg-card/70 backdrop-blur">
      <CardHeader>
        <CardTitle className="font-display text-xl font-semibold">
          Correlation ID
        </CardTitle>
        <CardDescription className="text-sm">
          Every request is tagged and echoed on ``X-Correlation-Id``.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-6 w-full" />
        ) : (
          <code
            data-testid="correlation-id-value"
            className="block truncate rounded-md border border-border bg-secondary px-3 py-2 font-mono text-xs text-secondary-foreground"
          >
            {correlationId ?? "—"}
          </code>
        )}
      </CardContent>
    </Card>
  );
}

function ErrorPanel({ error }: { error: ApiError | Error }): JSX.Element {
  const isApi = error instanceof ApiError;
  return (
    <div
      data-testid="error-panel"
      className="mt-8 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
      role="alert"
    >
      <p className="font-medium">Health check failed</p>
      <p className="mt-1 text-xs text-destructive/80">
        {isApi ? `${error.code} — ` : ""}
        {error.message}
      </p>
    </div>
  );
}
